import { useState, useEffect } from 'react';
import { Crown, Check, HelpCircle } from 'lucide-react';

const SETTING_KEY = 'premium_providers_intro';

export default function AdminSiteSettingsTab() {
  const [value, setValue] = useState('');
  const [savedValue, setSavedValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const fetchSetting = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/v1/site-settings/${SETTING_KEY}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('소개 문구를 불러오는데 실패했습니다.');
      }
      const data = await response.json();
      const text: string = data.data?.value ?? '';
      setValue(text);
      setSavedValue(text);
    } catch (err) {
      setError(err instanceof Error ? err.message : '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSetting();
  }, []);

  const isDirty = value !== savedValue;

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const response = await fetch(`/api/v1/admin/site-settings/${SETTING_KEY}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ value }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || '저장에 실패했습니다.');
      }
      setSavedValue(value);
      setMessage({ type: 'success', text: '소개 문구가 저장되었습니다.' });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : '오류가 발생했습니다.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* 헤더 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Crown className="w-6 h-6 text-accent" />
          우수 인증 교육 기관 소개
        </h1>
        <p className="text-gray-500 mt-1">
          메인페이지 "우수 인증 교육 기관" 제목 옆 물음표 아이콘에 마우스를 올리면 표시되는 소개 문구입니다.
        </p>
      </div>

      {/* 메시지 */}
      {message && (
        <div
          className={`mb-4 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center text-gray-500">
          불러오는 중...
        </div>
      ) : error ? (
        <div className="bg-red-50 text-red-700 rounded-xl border border-red-200 p-6">{error}</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 편집 */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">소개 문구</label>
            <textarea
              value={value}
              onChange={(e) => setValue(e.target.value)}
              rows={12}
              placeholder="우수 인증 교육 기관에 대한 소개 문구를 입력하세요."
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm leading-relaxed resize-y focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-gray-400">{value.length}자 · 줄바꿈은 그대로 표시됩니다.</span>
              <button
                onClick={handleSave}
                disabled={!isDirty || saving}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Check className="w-4 h-4" />
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>

          {/* 미리보기 */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <span className="block text-sm font-medium text-gray-700 mb-3">미리보기</span>
            <div className="flex items-center gap-2 mb-4">
              <Crown className="w-5 h-5 text-accent" />
              <h2 className="text-xl font-bold text-gray-900">우수 인증 교육 기관</h2>
              <HelpCircle className="w-4 h-4 text-gray-400" />
            </div>
            <div className="rounded-lg bg-gray-900 text-white text-sm leading-relaxed p-4 whitespace-pre-line max-w-md">
              {value.trim() || '소개 문구가 비어 있습니다.'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
